import React, { useState, useEffect, useId } from 'react';
import { Plus, Edit, Trash2, Save, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import TeamProjectsPanel from './TeamProjectsPanel';
import * as fmt from '../../lib/format';

const ALLOWED_GROUPS = ['Business Team', 'Developers', 'Ops and MIS', 'Product Managers', 'QA Team', 'DevOps', 'Designer'];

export default function ManageTeamsTab() {
  const uid = useId();
  const { showToast } = useToast();
  const confirm = useConfirm();
  // Route all feedback to the global toast (strip the leading ✅/❌ marker).
  const notify = (msg: string) =>
    showToast(msg.startsWith('❌') ? 'error' : 'success', msg.replace(/^[✅❌]\s*/, ''));

  const [teams, setTeams] = useState<any[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);
  const [jiraUsers, setJiraUsers] = useState<any[]>([]);
  const [teamForm, setTeamForm] = useState<{ name: string; assignedUserIds: number[] }>({ name: '', assignedUserIds: [] });
  const [editingTeam, setEditingTeam] = useState<number | null>(null);
  const [editTeamForm, setEditTeamForm] = useState<{ name: string; assignedUserIds: number[] }>({ name: '', assignedUserIds: [] });
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const [teamMembers, setTeamMembers] = useState<Record<number, any[]>>({});
  const [addMemberTeamId, setAddMemberTeamId] = useState<number | null>(null);
  const [selectedMemberAccountId, setSelectedMemberAccountId] = useState('');

  const [groups, setGroups] = useState<string[]>([]);
  const [memberGroupFilter, setMemberGroupFilter] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  const [teamFormMembers, setTeamFormMembers] = useState<string[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);

  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberDropdownOpen, setAddMemberDropdownOpen] = useState(false);

  // Searchable "Assigned Users" pickers (create + edit forms).
  const [createAssignedSearch, setCreateAssignedSearch] = useState('');
  const [createAssignedOpen, setCreateAssignedOpen] = useState(false);
  const [editAssignedSearch, setEditAssignedSearch] = useState('');
  const [editAssignedOpen, setEditAssignedOpen] = useState(false);

  const visibleGroups = groups.filter(g => ALLOWED_GROUPS.includes(g));

  useEffect(() => {
    loadUsers();
    loadTeams();
    loadJiraUsers();
    loadGroups();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiService.getAssignableUsers();
      if (response.data.success) {
        setAssignableUsers(response.data.data.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const toggleAssignedUser = (
    setForm: React.Dispatch<React.SetStateAction<any>>,
    id: number
  ) =>
    setForm((prev: { assignedUserIds: number[] }) => ({
      ...prev,
      assignedUserIds: prev.assignedUserIds.includes(id)
        ? prev.assignedUserIds.filter((x) => x !== id)
        : [...prev.assignedUserIds, id],
    }));

  const userLabel = (id: number) => {
    const u = assignableUsers.find((x) => x.id === id);
    return u?.displayName || u?.username || `#${id}`;
  };

  // Searchable multi-select dropdown for assigning Users to a team.
  const renderAssignedUserPicker = (
    selectedIds: number[],
    setForm: React.Dispatch<React.SetStateAction<any>>,
    search: string,
    setSearch: (v: string) => void,
    open: boolean,
    setOpen: (v: boolean) => void,
    inputId: string,
  ) => {
    const q = search.toLowerCase();
    const matches = assignableUsers.filter((u) => {
      if (selectedIds.includes(u.id)) return false;
      if (!q) return true;
      return (u.displayName || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    }).slice(0, 30);

    return (
      <div>
        {/* Search input + dropdown */}
        <div className="relative">
          <input
            id={inputId}
            type="text"
            placeholder={assignableUsers.length === 0 ? 'No users available — create User-role accounts first' : 'Search users to assign…'}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            disabled={assignableUsers.length === 0}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          />
          {open && assignableUsers.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {matches.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500">No users found</p>
              ) : (
                matches.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={() => { toggleAssignedUser(setForm, u.id); setSearch(''); setOpen(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0"
                  >
                    <span className="font-medium text-gray-900">{u.displayName || u.username}</span>
                    {u.email && <span className="ml-2 text-gray-500">{u.email}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Selected chips — shown below the input so the dropdown isn't pushed down */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                {userLabel(id)}
                <button type="button" onClick={() => toggleAssignedUser(setForm, id)} aria-label={`Remove ${userLabel(id)}`} className="hover:text-blue-600">
                  <XCircle className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const loadTeams = async () => {
    try {
      const response = await apiService.getAllTeams();
      if (response.data.success) {
        setTeams(response.data.data.teams);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const loadJiraUsers = async () => {
    try {
      const response = await apiService.getJiraUsers();
      if (response.data?.success) {
        setJiraUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load Jira users:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await apiService.getDistinctUserGroups();
      if (response.data?.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load user groups:', error);
    }
  };

  const loadTeamMembers = async (teamId: number) => {
    try {
      const response = await apiService.getTeamMembers(teamId);
      if (response.data.success) {
        setTeamMembers(prev => ({ ...prev, [teamId]: response.data.data.members }));
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamForm.name) {
      notify('❌ Team name is required');
      return;
    }
    try {
      const response = await apiService.createTeam({ name: teamForm.name, assigned_user_ids: teamForm.assignedUserIds });
      if (response.data.success) {
        const newTeamId = response.data.data?.team?.id;
        // Add selected members if any
        if (newTeamId && teamFormMembers.length > 0) {
          await Promise.all(teamFormMembers.map(accountId => apiService.addTeamMember(newTeamId, accountId)));
        }
        notify(`✅ Team created${teamFormMembers.length > 0 ? ` with ${teamFormMembers.length} member(s)` : ''}!`);
        setTeamForm({ name: '', assignedUserIds: [] });
        setCreateAssignedSearch('');
        setCreateAssignedOpen(false);
        setTeamFormMembers([]);
        setMemberSearchQuery('');
        loadTeams();
      } else {
        notify(`❌ ${response.data.error || 'Failed to create team'}`);
      }
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    const team = teams.find((t) => t.id === teamId);
    const yes = await confirm({
      title: team?.name ? `Delete the team ${team.name}?` : 'Delete this team?',
      message: 'This cannot be undone.',
      confirmLabel: 'Delete team',
    });
    if (!yes) return;
    try {
      await apiService.deleteTeam(teamId);
      notify('✅ Team deleted successfully!');
      setTeams(prev => prev.filter(t => t.id !== teamId));
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    }
  };

  const handleStartEditTeam = (team: any) => {
    setEditingTeam(team.id);
    setEditTeamForm({ name: team.name, assignedUserIds: (team.assigned_users || []).map((u: any) => u.id) });
    setEditAssignedSearch('');
    setEditAssignedOpen(false);
  };

  const handleSaveEditTeam = async (teamId: number) => {
    try {
      const response = await apiService.updateTeam(teamId, {
        name: editTeamForm.name,
        assigned_user_ids: editTeamForm.assignedUserIds
      });
      if (response.data.success) {
        notify('✅ Team updated successfully!');
        setEditingTeam(null);
        loadTeams();
      } else {
        notify(`❌ ${response.data.error || 'Failed to update team'}`);
      }
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    }
  };

  const handleToggleTeamMembers = async (teamId: number) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      await loadTeamMembers(teamId);
    }
  };

  const handleAddMember = async (teamId: number) => {
    if (!selectedMemberAccountId) {
      notify('❌ Please select a user to add');
      return;
    }
    try {
      await apiService.addTeamMember(teamId, selectedMemberAccountId);
      notify('✅ Member added!');
      setSelectedMemberAccountId('');
      setAddMemberTeamId(null);
      setAddMemberSearch('');
      setAddMemberDropdownOpen(false);
      await loadTeamMembers(teamId);
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRemoveMember = async (teamId: number, accountId: string) => {
    try {
      await apiService.removeTeamMember(teamId, accountId);
      notify('✅ Member removed!');
      setTeamMembers(prev => ({
        ...prev,
        [teamId]: (prev[teamId] || []).filter(m => m.jira_user_account_id !== accountId)
      }));
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Manage teams</h3>
        <p className="text-sm text-gray-600">Create teams, add Jira members, and set the team manager scoped to each team</p>
      </div>

      {/* Create team Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Create new team</h4>
        <div className="space-y-3">
          {/* Team name + Team manager (same row) */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1">
              <label htmlFor={`${uid}-1`} className="block text-xs font-medium text-gray-700 mb-1">Team name</label>
              <input
                id={`${uid}-1`}
                type="text"
                placeholder="Team name"
                value={teamForm.name}
                onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label htmlFor={`${uid}-create-manager`} className="block text-xs font-medium text-gray-700 mb-1">
                Team manager <span className="font-normal text-gray-500">(scoped to this team's members)</span>
              </label>
              {renderAssignedUserPicker(
                teamForm.assignedUserIds,
                setTeamForm,
                createAssignedSearch,
                setCreateAssignedSearch,
                createAssignedOpen,
                setCreateAssignedOpen,
                `${uid}-create-manager`,
              )}
            </div>
          </div>

          {/* Searchable member picker */}
          <div>
            <label htmlFor={`${uid}-members`} className="block text-xs font-medium text-gray-700 mb-1">Add members</label>

            {/* Selected member chips */}
            {teamFormMembers.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {teamFormMembers.map(accountId => {
                  const member = jiraUsers.find(u => u.account_id === accountId);
                  return (
                    <span key={accountId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {member?.display_name || member?.email_address || accountId}
                      <button
                        type="button"
                        onClick={() => setTeamFormMembers(prev => prev.filter(id => id !== accountId))}
                        aria-label={`Remove ${member?.display_name || member?.email_address || accountId}`}
                        className="hover:text-blue-600"
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Active/Inactive filter for member picker */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-500 shrink-0">Status:</span>
              <div className="flex gap-1">
                {(['all', 'active', 'inactive'] as const).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setMemberStatusFilter(s)}
                    className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberStatusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Group filter chips for member picker */}
            <div className="flex items-start gap-2 mb-2">
              <span className="text-xs font-medium text-gray-500 shrink-0 mt-0.5">Group:</span>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setMemberGroupFilter('')}
                  className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberGroupFilter === '' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}
                >All</button>
                {visibleGroups.map(g => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setMemberGroupFilter(g === memberGroupFilter ? '' : g)}
                    className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberGroupFilter === g ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Search input + dropdown */}
            <div className="relative">
              <input
                id={`${uid}-members`}
                type="text"
                placeholder="Search and add members..."
                value={memberSearchQuery}
                onChange={(e) => { setMemberSearchQuery(e.target.value); setMemberDropdownOpen(true); }}
                onFocus={() => setMemberDropdownOpen(true)}
                onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {memberDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {(() => {
                    const filtered = jiraUsers.filter(u => {
                      if (teamFormMembers.includes(u.account_id)) return false;
                      const q = memberSearchQuery.toLowerCase();
                      const matchSearch = !q ||
                        (u.display_name || '').toLowerCase().includes(q) ||
                        (u.email_address || '').toLowerCase().includes(q);
                      const matchStatus = memberStatusFilter === 'all' ||
                        (memberStatusFilter === 'active' && u.active !== false) ||
                        (memberStatusFilter === 'inactive' && u.active === false);
                      const matchGroup = !memberGroupFilter ||
                        (Array.isArray(u.groups) && u.groups.includes(memberGroupFilter));
                      return matchSearch && matchStatus && matchGroup;
                    }).slice(0, 30);
                    return filtered.length === 0
                      ? <p className="px-3 py-2 text-xs text-gray-500">No users found</p>
                      : filtered.map(u => (
                        <button
                          key={u.account_id}
                          type="button"
                          onMouseDown={() => {
                            setTeamFormMembers(prev => [...prev, u.account_id]);
                            setMemberSearchQuery('');
                            setMemberDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium text-gray-900">{u.display_name || u.account_id}</span>
                          {u.email_address && <span className="ml-2 text-gray-500">{u.email_address}</span>}
                          {u.active === false && <span className="ml-1 text-xs text-gray-500">(inactive)</span>}
                        </button>
                      ));
                  })()}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCreateTeam}
            className="flex items-center px-3 py-2 bg-green-700 text-white text-xs rounded-lg hover:bg-green-800"
          >
            <Plus className="w-3 h-3 mr-1" />
            Create team
          </button>
        </div>
      </div>

      {/* Teams List */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing teams</h4>
        {teams.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No teams yet. Create one above.</p>
        ) : (
          <div className="space-y-2">
            {teams.map((team) => (
              <div key={team.id} className="border border-gray-200 rounded-lg">
                {editingTeam === team.id ? (
                  <div className="p-3 space-y-2">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="flex-1">
                        <label htmlFor={`${uid}-2`} className="block text-xs font-medium text-gray-700 mb-1">Team name</label>
                        <input
                          id={`${uid}-2`}
                          type="text"
                          value={editTeamForm.name}
                          onChange={(e) => setEditTeamForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="Team name"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={`${uid}-edit-manager`} className="block text-xs font-medium text-gray-700 mb-1">Team manager</label>
                        {renderAssignedUserPicker(
                          editTeamForm.assignedUserIds,
                          setEditTeamForm,
                          editAssignedSearch,
                          setEditAssignedSearch,
                          editAssignedOpen,
                          setEditAssignedOpen,
                          `${uid}-edit-manager`, // only one team is edited at a time
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleSaveEditTeam(team.id)} className="flex items-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">
                        <Save className="h-3 w-3 mr-1" />Save
                      </button>
                      <button onClick={() => setEditingTeam(null)} className="flex items-center px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">
                        <XCircle className="h-3 w-3 mr-1" />Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between px-3 py-2">
                      <div>
                        <span className="text-xs font-medium text-gray-900">{team.name}</span>
                        <span className="text-xs text-gray-500 ml-2">Manager: {(team.assigned_users || []).map((u: any) => u.displayName || u.username).join(', ') || fmt.MISSING}</span>
                        <span className="text-xs text-gray-500 ml-2">({team.member_count} members)</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => handleToggleTeamMembers(team.id)} className="p-1 text-gray-500 hover:text-gray-700" title="View members" aria-label={`Members of ${team.name}`} aria-expanded={expandedTeam === team.id}>
                          {expandedTeam === team.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <button onClick={() => handleStartEditTeam(team)} className="p-1 text-gray-500 hover:text-blue-600" title="Edit team" aria-label={`Edit ${team.name}`}>
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteTeam(team.id)} className="p-1 text-gray-500 hover:text-red-600" title="Delete team" aria-label={`Delete ${team.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded members panel */}
                    {expandedTeam === team.id && (
                      <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
                        <div className="space-y-1 mb-2">
                          {(teamMembers[team.id] || []).length === 0 ? (
                            <p className="text-xs text-gray-500">No members yet</p>
                          ) : (
                            (teamMembers[team.id] || []).map(member => (
                              <div key={member.jira_user_account_id} className="flex items-center justify-between">
                                <span className="text-xs text-gray-700">{member.display_name || member.jira_user_account_id}</span>
                                <button
                                  onClick={() => handleRemoveMember(team.id, member.jira_user_account_id)}
                                  className="p-0.5 text-gray-500 hover:text-red-600"
                                  title="Remove member" aria-label={`Remove ${member.display_name || member.jira_user_account_id}`}
                                >
                                  <XCircle className="h-3 w-3" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Add member inline — searchable dropdown */}
                        {addMemberTeamId === team.id ? (
                          <div className="mt-2 space-y-1">
                            {/* Group + status quick filters for add-member */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 shrink-0">Status:</span>
                              <div className="flex flex-wrap gap-1">
                                {(['all', 'active', 'inactive'] as const).map(s => (
                                  <button
                                    key={s}
                                    type="button"
                                    onClick={() => setMemberStatusFilter(s)}
                                    className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberStatusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                                  >
                                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-gray-500 shrink-0 mt-0.5">Group:</span>
                              <div className="flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  onClick={() => setMemberGroupFilter('')}
                                  className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberGroupFilter === '' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}
                                >All</button>
                                {visibleGroups.map(g => (
                                  <button
                                    key={g}
                                    type="button"
                                    onClick={() => setMemberGroupFilter(g === memberGroupFilter ? '' : g)}
                                    className={`hit-area-sm px-2 py-0.5 text-xs rounded-full border transition-colors ${memberGroupFilter === g ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}
                                  >{g}</button>
                                ))}
                              </div>
                            </div>
                            {/* Searchable dropdown */}
                            <div className="relative">
                              <input
                                type="text"
                                aria-label="Search for a Jira person to add to this team"
                                placeholder="Search Jira user to add..."
                                value={addMemberSearch}
                                onChange={(e) => { setAddMemberSearch(e.target.value); setAddMemberDropdownOpen(true); }}
                                onFocus={() => setAddMemberDropdownOpen(true)}
                                onBlur={() => setTimeout(() => setAddMemberDropdownOpen(false), 150)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              />
                              {addMemberDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
                                  {(() => {
                                    const existing = teamMembers[team.id] || [];
                                    const filtered = jiraUsers.filter(ju => {
                                      if (existing.some(m => m.jira_user_account_id === ju.account_id)) return false;
                                      const q = addMemberSearch.toLowerCase();
                                      const matchSearch = !q ||
                                        (ju.display_name || '').toLowerCase().includes(q) ||
                                        (ju.email_address || '').toLowerCase().includes(q);
                                      const matchStatus = memberStatusFilter === 'all' ||
                                        (memberStatusFilter === 'active' && ju.active !== false) ||
                                        (memberStatusFilter === 'inactive' && ju.active === false);
                                      const matchGroup = !memberGroupFilter ||
                                        (Array.isArray(ju.groups) && ju.groups.includes(memberGroupFilter));
                                      return matchSearch && matchStatus && matchGroup;
                                    }).slice(0, 30);
                                    return filtered.length === 0
                                      ? <p className="px-3 py-2 text-xs text-gray-500">No users found</p>
                                      : filtered.map(ju => (
                                        <button
                                          key={ju.account_id}
                                          type="button"
                                          onMouseDown={() => {
                                            setSelectedMemberAccountId(ju.account_id);
                                            setAddMemberSearch(ju.display_name || ju.email_address || '');
                                            setAddMemberDropdownOpen(false);
                                          }}
                                          className={`w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0 ${selectedMemberAccountId === ju.account_id ? 'bg-blue-50' : ''}`}
                                        >
                                          <span className="font-medium text-gray-900">{ju.display_name || ju.account_id}</span>
                                          {ju.email_address && <span className="ml-2 text-gray-500">{ju.email_address}</span>}
                                          {ju.active === false && <span className="ml-1 text-xs text-gray-500">(inactive)</span>}
                                        </button>
                                      ));
                                  })()}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => handleAddMember(team.id)} className="px-2 py-1 bg-green-700 text-white text-xs rounded hover:bg-green-800">Add</button>
                              <button onClick={() => { setAddMemberTeamId(null); setSelectedMemberAccountId(''); setAddMemberSearch(''); setAddMemberDropdownOpen(false); }} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddMemberTeamId(team.id); setSelectedMemberAccountId(''); setAddMemberSearch(''); setAddMemberDropdownOpen(false); }}
                            className="flex items-center text-xs text-blue-600 hover:text-blue-800 mt-1"
                          >
                            <Plus className="h-3 w-3 mr-1" />Add member
                          </button>
                        )}
                        <TeamProjectsPanel teamId={team.id} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
